import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  getChallenges,
  getMyChallengePoints,
  recordMissionEvent,
  submitChallengePick,
} from '@/lib/data/provider';

/** Open + recent fantasy challenges, with the caller's pick + the crowd tally. */
export function useChallenges() {
  return useQuery({
    queryKey: ['challenges', 'list'],
    queryFn: getChallenges,
    staleTime: QUERY_STALE_TIME.live,
    enabled: isSupabaseConfigured,
  });
}

/** The caller's running total of challenge bonus points. */
export function useMyChallengePoints() {
  const { userId } = useAuth();
  return useQuery({
    queryKey: ['challenges', 'my-points', userId ?? 'anon'],
    queryFn: getMyChallengePoints,
    staleTime: QUERY_STALE_TIME.user,
    enabled: isSupabaseConfigured && Boolean(userId),
  });
}

export function useSubmitChallengePick() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { challengeId: string; optionId: string }) =>
      submitChallengePick(input.challengeId, input.optionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      void recordMissionEvent('challenge')
        .then(() => queryClient.invalidateQueries({ queryKey: ['rewards'] }))
        .catch(() => {});
    },
  });
}
